import React, { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  notification,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { SaveOutlined } from '@ant-design/icons';
import Request from 'common/network/Request';
import potentialCandidateApis from 'modules/dashboard/api/potential-candidate';
import _ from 'lodash';
import { getFirstFieldValidateErrorFromResponse } from 'common/utils/network';
import SelectTime from 'components/form/elements/SelectTime';
import Note from 'components/form/elements/Note';
import UploadImages from 'components/form/elements/UploadImages';

const NewRecordForm = ({ onSuccess, record }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    watch,
  } = useForm({
    defaultValues: record,
  });

  const formData = watch();

  const handleCreate = async (data) => {
    setLoading(true);
    try {
      const response = await Request.post(
        record
          ? potentialCandidateApis.update_candidate
          : potentialCandidateApis.new_candidate,
        {
          ...data,
          time: data.time ? data.time.clone().unix() : undefined,
          images: _.map(data.images, (image) => ({
            id: image.id,
            path: image.url,
          })),
        },
      );
      if (response && response?.success) {
        message.success(response?.message || 'Thêm mới ứng viên thành công.');
        if (typeof onSuccess === 'function') {
          onSuccess(response);
        }
      } else {
        let mess;
        if (_.isObject(response?.message)) {
          const customOrder = [
            'name',
            'phone',
            'address',
            'job',
            'monthly_income',
            'marital_status',
          ];
          const { message, firstField } =
            getFirstFieldValidateErrorFromResponse(response, customOrder);

          if (firstField) {
            mess = message;
            setError(firstField, { message: mess });
          }
        } else {
          mess = response?.message || 'Không thể thêm mới. Vui lòng thử lại.';
        }

        notification.error({
          message: 'Lỗi',
          description: mess,
        });
        setErrorMessage(mess);
      }
    } catch (error) {
      const errorMessage =
        error.response?.message || 'Không thể thêm mới. Vui lòng thử lại.';
      notification.error({
        message: 'Lỗi',
        description: errorMessage,
      });
      setErrorMessage(errorMessage);
    }
    setLoading(false);
  };

  const required = (message) => ({
    required: message,
  });

  return (
    <div className="py-4">
      <Form
        colon={false}
        onFinish={handleSubmit(handleCreate)}
        layout="vertical"
      >
        <Form.Item
          label="Họ và tên"
          required
          validateStatus={errors?.name ? 'error' : undefined}
          help={errors?.name?.message}
        >
          <Controller
            render={({ field }) => (
              <Input {...field} placeholder="Nguyễn Văn A" />
            )}
            name="name"
            control={control}
            rules={required('Họ và tên không được để trống')}
          />
        </Form.Item>
        <Form.Item
          label="SĐT"
          required
          validateStatus={errors?.phone ? 'error' : undefined}
          help={errors?.phone?.message}
        >
          <Controller
            render={({ field }) => (
              <Input {...field} placeholder="0989123456" />
            )}
            name="phone"
            control={control}
            rules={{
              ...required('SĐT không được để trống'),
              pattern: {
                value: /(?:\+84|0084|0)[235789][0-9]{1,2}[0-9]{7}(?:\D+|$)/g,
                message: 'SĐT không hợp lệ',
              },
            }}
          />
        </Form.Item>
        <Form.Item
          label="Địa chỉ"
          required
          validateStatus={errors?.address ? 'error' : undefined}
          help={errors?.address?.message}
        >
          <Controller
            render={({ field }) => <Input {...field} placeholder="Hà Nội" />}
            name="address"
            control={control}
            rules={required('Địa chỉ không được để trống')}
          />
        </Form.Item>
        {!record ? (
          <div className="pt-3">
            <Card
              size="small"
              title="Gặp gỡ lần đầu (nếu đã gặp)"
              className="mb-6"
            >
              <SelectTime
                control={control}
                errors={errors}
                required={!!formData.note}
              />
              <Note
                label="Nội dung gặp gỡ"
                name="note"
                control={control}
                rules={{
                  ...(formData.time
                    ? { required: 'Nội dung không được để trống' }
                    : {}),
                  maxLength: {
                    value: 500,
                    message: 'Nội dung không được vượt quá 500 ký tự!',
                  },
                }}
                errors={errors}
                required={!!formData.time}
              />
              <UploadImages
                control={control}
                setError={setError}
                errors={errors}
                data={formData}
                onUploading={setUploading}
              />
            </Card>
          </div>
        ) : null}
        <Form.Item
          label="Nghề nghiệp"
          validateStatus={errors?.job ? 'error' : undefined}
          help={errors?.job?.message}
        >
          <Controller
            render={({ field }) => (
              <Input {...field} placeholder="Nhân viên kinh doanh" />
            )}
            name="job"
            control={control}
          />
        </Form.Item>
        <Form.Item
          label="Thu nhập hàng tháng"
          validateStatus={errors?.monthly_income ? 'error' : undefined}
          help={errors?.monthly_income?.message}
        >
          <Controller
            render={({ field }) => (
              <InputNumber
                {...field}
                className="w-full"
                min={0}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                }
                parser={(value) => value?.replace(/( *)/g, '')}
                placeholder="100 000 000"
              />
            )}
            name="monthly_income"
            control={control}
            rules={{
              min: {
                value: 0,
                message: 'Thu nhập hàng tháng không được nhỏ hơn 0',
              },
            }}
          />
        </Form.Item>
        <Form.Item
          label="Tình trạng gia đình"
          validateStatus={errors?.marital_status ? 'error' : undefined}
          help={errors?.marital_status?.message}
        >
          <Controller
            render={({ field }) => <Input {...field} placeholder="Độc thân" />}
            name="marital_status"
            control={control}
          />
        </Form.Item>
        {errorMessage && (
          <div
            className="py-2 px-4 my-4 text-sm text-red-800 rounded-lg bg-red-50 text-center"
            role="alert"
          >
            <span className="">{errorMessage}</span>
          </div>
        )}
        <Button
          type="primary"
          htmlType="submit"
          block
          loading={loading}
          icon={<SaveOutlined />}
          disabled={uploading}
        >
          {record ? 'Cập nhật' : 'Thêm mới'}
        </Button>
      </Form>
    </div>
  );
};

export default NewRecordForm;
